import React, { useMemo, useState } from 'react';
import { Button, Card, Space, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../../../ui';
import type { Route } from '../../../types';
import type { RouteViewModelDocuments, RouteViewModelDocumentRequirement } from '../../../types/routeViewModel';
import { documentTypeLabel } from '../../../utils/enumUiLabels';
import { resolveDocumentDisplayTypeLabel } from '../../../utils/documentDisplayName';
import UploadDocumentModal from '../../documents/UploadDocumentModal';
import {
  downloadDocument,
  resolveDocumentFilename,
  viewDocument,
} from '../../../utils/documentDownload';
import {
  routeDocMatchesRequirement,
  uploadPresetForRouteRequirement,
  type RouteDocumentUploadPreset,
} from './routeDocumentsShared';
import {
  isBusinessDocumentDefinitionActive,
  listBusinessDocumentDefinitions,
  normalizeBusinessTransportMode,
  type BusinessDocumentDefinition,
} from '../../../utils/businessDocumentDefinitions';

const { Text, Title } = Typography;

type RawRouteDocument = Record<string, unknown> & {
  id?: number;
  documentType?: string;
  customDocumentName?: string | null;
  filePath?: string;
  createdAt?: string;
  transportConsignmentNoteId?: number;
  tnId?: number;
  isOriginalTN?: boolean;
};

type RequirementRow = {
  key: string;
  rowType: 'requirement';
  definition: BusinessDocumentDefinition;
  req: RouteViewModelDocumentRequirement | null;
  doc: RawRouteDocument | null;
  active: boolean;
};

type RawDocumentRow = {
  key: string;
  rowType: 'raw';
  doc: RawRouteDocument;
};

type Row = RequirementRow | RawDocumentRow;
type GroupedRows = { key: string; title: string; rows: Row[] };

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : '—';
}

function requirementStatusMeta(row: RequirementRow): { label: string; color: string; hint: string | null } {
  if (row.doc?.id != null) return { label: 'Загружен', color: 'green', hint: null };
  if (!row.active) return { label: 'Недоступен', color: 'default', hint: 'Этап еще не наступил' };
  if (row.req?.status === 'OVERDUE') return { label: 'Просрочен', color: 'red', hint: row.req.hint };
  return { label: 'Доступен', color: 'blue', hint: row.req?.hint ?? null };
}

function rawDocumentTitle(doc: RawRouteDocument): string {
  const transportId =
    typeof doc.transportConsignmentNoteId === 'number'
      ? doc.transportConsignmentNoteId
      : typeof doc.tnId === 'number'
        ? doc.tnId
        : null;
  if (transportId != null) {
    return `ТН #${transportId}`;
  }
  const docType = String(doc.documentType || '').trim();
  return docType
    ? resolveDocumentDisplayTypeLabel(doc, documentTypeLabel)
    : 'Документ';
}

function rawDocumentName(doc: RawRouteDocument): string {
  if (doc.id == null && !doc.filePath && !(doc.content as { originalName?: string } | undefined)?.originalName) {
    return '—';
  }
  return resolveDocumentFilename(doc);
}

function rawDocumentStageLabel(doc: RawRouteDocument): string {
  const stage = String(doc.stage || '').toLowerCase();
  if (stage === 'loading') return 'Погрузка';
  if (stage === 'unloading') return 'Выгрузка';
  if (stage === 'other') return 'Другое';
  return '—';
}

async function downloadRouteDocument(doc: RawRouteDocument): Promise<void> {
  if (typeof doc.id !== 'number') return;
  await downloadDocument(doc.id, doc);
}

async function viewRouteDocument(doc: RawRouteDocument): Promise<void> {
  if (typeof doc.id !== 'number') return;
  await viewDocument(doc.id);
}

function openRawDocument(doc: RawRouteDocument, navigate: ReturnType<typeof useNavigate>): void {
  const transportId =
    typeof doc.transportConsignmentNoteId === 'number'
      ? doc.transportConsignmentNoteId
      : typeof doc.tnId === 'number'
        ? doc.tnId
        : null;

  if (transportId != null) {
    navigate(`/tn/${transportId}/view`);
    return;
  }

  void viewRouteDocument(doc).catch(() => message.error('Не удалось открыть файл'));
}

function rowDocumentType(row: Row): string {
  if (row.rowType === 'requirement') {
    return String(row.doc?.documentType || row.definition.legacyDocumentType || row.definition.key || '').toUpperCase();
  }
  return String(row.doc.documentType || '').toUpperCase();
}

function carrierContractMetaExists(route: Route | null | undefined): boolean {
  return Boolean(
    route?.carrierContractRequestNumber?.trim() ||
      route?.carrierContractRequestDate ||
      route?.carrierContractRequestFile ||
      route?.carrierAgreement?.number ||
      route?.carrierAgreement?.date
  );
}

function carrierContractDisplayName(route: Route | null | undefined): string {
  const number = route?.carrierContractRequestNumber?.trim() || route?.carrierAgreement?.number || null;
  if (number) return `№${number}`;
  return 'Реквизиты договора-заявки';
}

function carrierContractDate(route: Route | null | undefined): string | null {
  return route?.carrierContractRequestDate || route?.carrierAgreement?.date || null;
}

function routeHasBusinessDocument(
  definition: BusinessDocumentDefinition,
  doc: RawRouteDocument | null,
  route: Route | null | undefined
): boolean {
  if (doc?.id != null) return true;
  return definition.key === 'CARRIER_CONTRACT_REQUEST' && carrierContractMetaExists(route);
}

function rowTypeSortLabel(row: Row): string {
  if (row.rowType === 'raw') {
    return resolveDocumentDisplayTypeLabel(row.doc, documentTypeLabel);
  }
  const doc = row.doc;
  if (doc) {
    return resolveDocumentDisplayTypeLabel(doc, documentTypeLabel);
  }
  return documentTypeLabel(rowDocumentType(row));
}

function rowDateValue(row: Row): number {
  const raw = row.rowType === 'requirement' ? row.doc?.createdAt : row.doc.createdAt;
  return raw ? dayjs(String(raw)).valueOf() : 0;
}

function rowGroup(row: Row): { key: string; title: string; order: number } {
  if (row.rowType === 'requirement') {
    switch (row.definition.processStage) {
      case 'basis':
        return { key: 'basis', title: 'Основание перевозки', order: 1 };
      case 'loading':
        return { key: 'loading', title: 'Погрузка', order: 2 };
      case 'unloading':
        return { key: 'unloading', title: 'Выгрузка', order: 3 };
      case 'closing':
      case 'settlement':
        return { key: 'closing', title: 'Закрытие маршрута', order: 4 };
      default:
        return { key: 'other', title: 'Прочее', order: 5 };
    }
  }

  const docType = rowDocumentType(row);
  const fileType = row.rowType === 'raw' ? String(row.doc.fileType || '').toLowerCase() : '';

  if (fileType === 'photo' || docType.includes('PHOTO')) {
    return { key: 'photos', title: 'Фото', order: 6 };
  }
  if (['CARRIER_INVOICE', 'LOADING_INVOICE', 'LOADING_TN', 'UNLOADING_DOC', 'UPD_ACT'].includes(docType)) {
    return { key: 'carrier-docs', title: 'Документы перевозчика', order: 7 };
  }
  if (['ORIGINAL_TN', 'TN_ORIGINAL', 'SIGNED_TN'].includes(docType)) {
    return { key: 'originals', title: 'Оригиналы', order: 8 };
  }
  return { key: 'other', title: 'Прочее', order: 9 };
}

export interface RouteDocumentsTabProps {
  model: RouteViewModelDocuments;
  route: Route | null;
  applications?: Record<string, unknown>[];
  onUploadSuccess?: () => void;
}

const RouteDocumentsTab: React.FC<RouteDocumentsTabProps> = ({ model, route, onUploadSuccess }) => {
  const navigate = useNavigate();
  const [uploadPreset, setUploadPreset] = useState<RouteDocumentUploadPreset | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const mergedRaw = useMemo<RawRouteDocument[]>(
    () => [
      ...((model.raw.documents || []) as RawRouteDocument[]),
      ...((model.raw.loadingDocuments || []) as RawRouteDocument[]),
      ...((model.raw.transportDocuments || []) as RawRouteDocument[]),
    ],
    [model.raw.documents, model.raw.loadingDocuments, model.raw.transportDocuments]
  );

  const rows = useMemo<Row[]>(() => {
    const matchedIds = new Set<number>();
    const requirementByKey = new Map(model.requirements.map((req) => [req.key, req]));
    const mode = normalizeBusinessTransportMode(route?.transportType);

    const requirementRows: RequirementRow[] = listBusinessDocumentDefinitions({
      party: 'CARRIER',
      mode,
    }).map((definition) => {
      const doc = mergedRaw.find((item) => routeDocMatchesRequirement(item, definition.key)) ?? null;
      if (typeof doc?.id === 'number') {
        matchedIds.add(doc.id);
      }
      return {
        key: `req-${definition.key}`,
        rowType: 'requirement',
        definition,
        req: requirementByKey.get(definition.key as RouteViewModelDocumentRequirement['key']) ?? null,
        doc,
        active: isBusinessDocumentDefinitionActive(definition, { route }),
      };
    });

    const rawRows: RawDocumentRow[] = mergedRaw
      .filter((doc) => !(typeof doc.id === 'number' && matchedIds.has(doc.id)))
      .sort((a, b) => {
        const at = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
        const bt = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
        return bt - at;
      })
      .map((doc, index) => ({
        key: typeof doc.id === 'number' ? `doc-${doc.id}` : `doc-extra-${index}`,
        rowType: 'raw',
        doc,
      }));

    return [...requirementRows, ...rawRows];
  }, [mergedRaw, model.requirements, route]);

  const groupedRows = useMemo<GroupedRows[]>(() => {
    const map = new Map<string, GroupedRows>();
    const orderMap = new Map<string, number>();

    const ensureGroup = (key: string, title: string, order: number) => {
      if (!map.has(key)) {
        map.set(key, { key, title, rows: [] });
        orderMap.set(key, order);
      }
      return map.get(key)!;
    };

    rows.forEach((row) => {
      const group = rowGroup(row);
      ensureGroup(group.key, group.title, group.order).rows.push(row);
    });

    return Array.from(map.values()).sort((a, b) => {
      return (orderMap.get(a.key) ?? 99) - (orderMap.get(b.key) ?? 99);
    }).map((group) => ({
      ...group,
      rows: [...group.rows].sort((a, b) => {
        const byType = rowTypeSortLabel(a).localeCompare(rowTypeSortLabel(b), 'ru');
        if (byType !== 0) return byType;
        return rowDateValue(b) - rowDateValue(a);
      }),
    }));
  }, [rows]);

  return (
    <Card size="small" title="Документы">
      <UploadDocumentModal
        visible={uploadOpen}
        preset={uploadPreset ?? undefined}
        route={route ?? undefined}
        onCancel={() => {
          setUploadOpen(false);
          setUploadPreset(null);
        }}
        onSuccess={() => {
          setUploadOpen(false);
          setUploadPreset(null);
          onUploadSuccess?.();
        }}
      />

      {groupedRows.length === 0 ? (
        <Text type="secondary">Документы отсутствуют</Text>
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {groupedRows.map((group) => (
            <div key={group.key}>
              <Title level={5} style={{ marginBottom: 8 }}>
                {group.title}
              </Title>
              <DataTable
                size="small"
                rowKey="key"
                pagination={false}
                dataSource={group.rows}
                columns={[
                  {
                    title: 'Тип документа',
                    key: 'type',
                    render: (_: unknown, row: Row) =>
                      row.rowType === 'requirement' ? row.definition.label : rawDocumentTitle(row.doc),
                  },
                  {
                    title: 'Название',
                    key: 'name',
                    render: (_: unknown, row: Row) =>
                      row.rowType === 'requirement'
                        ? routeHasBusinessDocument(row.definition, row.doc, route)
                          ? row.doc
                            ? rawDocumentName(row.doc)
                            : carrierContractDisplayName(route)
                          : '—'
                        : rawDocumentName(row.doc),
                  },
                  {
                    title: 'Этап',
                    key: 'stage',
                    width: 120,
                    render: (_: unknown, row: Row) =>
                      row.rowType === 'requirement' ? rowGroup(row).title : rawDocumentStageLabel(row.doc),
                  },
                  {
                    title: 'Статус',
                    key: 'status',
                    width: 140,
                    render: (_: unknown, row: Row) => {
                      if (row.rowType === 'requirement') {
                        const meta = requirementStatusMeta(row);
                        return <Tag color={meta.color}>{meta.label}</Tag>;
                      }
                      return <Tag color="blue">Загружен</Tag>;
                    },
                  },
                  {
                    title: 'Дата',
                    key: 'date',
                    width: 180,
                    render: (_: unknown, row: Row) =>
                      row.rowType === 'requirement'
                        ? formatDate((row.doc?.createdAt as string | undefined) ?? (row.definition.key === 'CARRIER_CONTRACT_REQUEST' ? carrierContractDate(route) : null))
                        : formatDate(row.doc.createdAt),
                  },
                  {
                    title: 'Действия',
                    key: 'actions',
                    width: 240,
                    render: (_: unknown, row: Row) => {
                      if (row.rowType === 'requirement') {
                        const preset = uploadPresetForRouteRequirement(row.definition.key);
                        const hasDoc = routeHasBusinessDocument(row.definition, row.doc, route);

                        return (
                          <Space size="small">
                            {hasDoc && row.doc ? (
                              <>
                                <Button size="small" type="link" onClick={() => openRawDocument(row.doc!, navigate)}>
                                  {row.doc?.transportConsignmentNoteId || row.doc?.tnId ? 'Открыть ТН' : 'Просмотр'}
                                </Button>
                                {row.doc?.transportConsignmentNoteId || row.doc?.tnId ? null : (
                                  <Button
                                    size="small"
                                    type="link"
                                    onClick={async () => {
                                      try {
                                        await downloadRouteDocument(row.doc!);
                                      } catch {
                                        message.error('Не удалось скачать файл');
                                      }
                                    }}
                                  >
                                    Скачать
                                  </Button>
                                )}
                              </>
                            ) : hasDoc ? (
                              <Text type="secondary">В реквизитах маршрута</Text>
                            ) : null}
                            {!hasDoc && row.active && route?.id && preset ? (
                              <Button
                                size="small"
                                type="link"
                                onClick={() => {
                                  setUploadPreset(preset);
                                  setUploadOpen(true);
                                }}
                              >
                                Загрузить
                              </Button>
                            ) : null}
                          </Space>
                        );
                      }

                      return (
                        <Space size="small">
                          <Button size="small" type="link" onClick={() => openRawDocument(row.doc, navigate)}>
                            {row.doc.transportConsignmentNoteId || row.doc.tnId ? 'Открыть ТН' : 'Просмотр'}
                          </Button>
                          {row.doc.transportConsignmentNoteId || row.doc.tnId ? null : (
                            <Button
                              size="small"
                              type="link"
                              onClick={async () => {
                                try {
                                  await downloadRouteDocument(row.doc);
                                } catch {
                                  message.error('Не удалось скачать файл');
                                }
                              }}
                            >
                              Скачать
                            </Button>
                          )}
                        </Space>
                      );
                    },
                  },
                ]}
              />
            </div>
          ))}
        </Space>
      )}
    </Card>
  );
};

export default RouteDocumentsTab;
